import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './services/guard/auth.guard';
import { AdminGuard } from './services/guard/admin.guard';
import { BusinessGuard } from './services/guard/business.guard';
import { LandingGuard } from './services/guard/landing.guard';

const routes: Routes = [
  {
    path: '',
    canActivate: [LandingGuard],
    loadChildren: () => import('./pages/marketing-home/marketing-home.module').then(m => m.MarketingHomePageModule)
  },
  {
    path: 'register',
    loadChildren: () => import('./pages/register/register.module').then(m => m.RegisterPageModule)
  },
  {
    path: 'plans',
    loadChildren: () => import('./pages/marketing-plans/marketing-plans.module').then(m => m.MarketingPlansPageModule)
  },
  {
    path: 'register-existing-user',
    loadChildren: () => import('./pages/register-existing-user/register-existing-user.module').then(m => m.RegisterExistingUserPageModule)
  },
  {
    path: 'login',
    loadChildren: () => import('./pages/login/login.module').then(m => m.LoginPageModule)
  },
  {
    path: 'oauth/authorize/login',
    loadChildren: () => import('./pages/login/login.module').then(m => m.LoginPageModule)
  },
  {
    path: 'privacy-policy',
    loadChildren: () => import('./pages/privacy-policy/privacy-policy.module').then(m => m.PrivacyPolicyPageModule)
  },
  {
    path: 'terms-of-service',
    loadChildren: () => import('./pages/terms-of-service/terms-of-service.module').then(m => m.TermsOfServicePageModule)
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./pages/home/home.module').then(m => m.HomePageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'home',
    redirectTo: 'dashboard',
    pathMatch: 'full'
  },
  {
    path: 'accounts/new',
    loadChildren: () => import('./pages/account-new/account-new.module').then(m => m.AccountNewPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'accounts',
    loadChildren: () => import('./pages/accounts/accounts.module').then(m => m.AccountsPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'post-new',
    loadChildren: () => import('./pages/post-new/post-new.module').then(m => m.PostNewPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'post-wordpress-new',
    loadChildren: () => import('./pages/post-wordpress-new/post-wordpress-new.module').then(m => m.PostWordpressNewPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'blog-new',
    loadChildren: () => import('./pages/blog-new/blog-new.module').then(m => m.BlogNewPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'settings',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'account',
        loadChildren: () => import('./pages/settings-account/settings-account.module').then(m => m.SettingsAccountPageModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'connect',
        loadChildren: () => import('./pages/settings-connect/settings-connect.module').then(m => m.SettingsConnectPageModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'connect-facebook',
        loadChildren: () => import('./pages/settings-connect-facebook/settings-connect-facebook.module').then(m => m.SettingsConnectFacebookPageModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'ai-tuning',
        loadChildren: () => import('./pages/settings-ai-tuning/settings-ai-tuning.module').then(m => m.SettingsAiTuningPageModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'ai-terminology',
        loadChildren: () => import('./pages/settings-ai-terminology/settings-ai-terminology.module').then(m => m.SettingsAiTerminologyPageModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'ai-writing-samples',
        loadChildren: () => import('./pages/settings-ai-writing-samples/settings-ai-writing-samples.module').then(m => m.SettingsAiWritingSamplesPageModule),
        canActivate: [AuthGuard]
      },
      // 'link-account' route removed — account linking replaced by Accounts feature
      {
        path: 'user',
        loadChildren: () => import('./pages/settings-user/settings-user.module').then(m => m.SettingsUserPageModule)
      },
      {
        path: 'user-management',
        loadChildren: () => import('./pages/settings-user-management/user-management.module').then( m => m.UserManagementPageModule),
        canActivate: [AuthGuard, BusinessGuard]
      },      
      {
        path: 'image-template',
        loadChildren: () => import('./pages/settings-image-template/settings-image-template.module').then(m => m.SettingsImageTemplatePageModule)
      },
      {
        path: 'billing',
        loadChildren: () => import('./pages/billing/billing.module').then(m => m.BillingPageModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'api-access',
        loadChildren: () => import('./pages/settings-api-access/settings-api-access.module').then(m => m.SettingsApiAccessPageModule),
        canActivate: [AuthGuard]
      },
    ]
  },
  {
    path: 'settings-api-access',
    redirectTo: '/settings/api-access',
    pathMatch: 'full'
  },
  {
    path: 'settings-ai-tuning',
    loadChildren: () => import('./pages/settings-ai-tuning/settings-ai-tuning.module').then(m => m.SettingsAiTuningPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'history',
    loadChildren: () => import('./pages/history/history.module').then(m => m.HistoryPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'history-post/:postId',
    loadChildren: () => import('./pages/history-post/history-post.module').then(m => m.HistoryPostPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'reports',
    loadChildren: () => import('./pages/reports/reports.module').then(m => m.ReportsPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'growth/cadence',
    loadChildren: () => import('./pages/growth-cadence/growth-cadence.module').then(m => m.GrowthCadencePageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'growth/themes',
    loadChildren: () => import('./pages/growth-themes/growth-themes.module').then(m => m.GrowthThemesPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'growth/engage',
    loadChildren: () => import('./pages/growth-engage/growth-engage.module').then(m => m.GrowthEngagePageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'growth/follower-quality',
    loadChildren: () => import('./pages/growth-follower-quality/growth-follower-quality.module').then(m => m.GrowthFollowerQualityPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'reset-password',
    loadChildren: () => import('./pages/reset-password/reset-password.module').then(m => m.ResetPasswordPageModule)
  },
  {
    path: 'reset-password-request',
    loadChildren: () => import('./pages/reset-password-request/reset-password-request.module').then(m => m.ResetPasswordRequestPageModule)
  },
  {
    path: 'aggregate-feed',
    canActivate: [AuthGuard],
    loadChildren: () => import('./pages/aggregate-feed/aggregate-feed.module').then(m => m.AggregateFeedPageModule)
  },
  {
    path: 'forgot-username',
    loadChildren: () => import('./pages/forgot-username/forgot-username.module').then(m => m.ForgotUsernamePageModule)
  },
  {
    path: 'admin',
    canActivate: [AuthGuard, AdminGuard],
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('./pages/admin-dashboard/admin-dashboard.module').then(m => m.AdminDashboardPageModule)
      },
      {
        path: 'logs',
        loadChildren: () => import('./pages/admin-logs/admin-logs.module').then(m => m.AdminLogsPageModule)
      },
      {
        path: 'aggregate-feed',
        loadChildren: () => import('./pages/admin-aggregate-feed/admin-aggregate-feed.module').then(m => m.AdminAggregateFeedPageModule)
      }
      ,
      {
        path: 'users',
        loadChildren: () => import('./pages/admin-users/admin-users.module').then(m => m.AdminUsersPageModule)
      },
      {
        path: 'user/:id',
        loadChildren: () => import('./pages/admin-user-detail/admin-user-detail.module').then( m => m.AdminUserDetailPageModule)
      },
      {
        path: 'tickets',
        loadChildren: () => import('./pages/admin-tickets/admin-tickets.module').then(m => m.AdminTicketsPageModule)
      },
      {
        path: 'tickets/:id',
        loadChildren: () => import('./pages/ticket-details/ticket-details.module').then(m => m.TicketDetailsPageModule)
      },
      {
        path: 'ai-activity',
        loadChildren: () => import('./pages/admin-ai-activity/admin-ai-activity.module').then(m => m.AdminAiActivityPageModule)
      },
      {
        path: 'ai-costs',
        loadChildren: () => import('./pages/admin-ai-costs/admin-ai-costs.module').then(m => m.AdminAiCostsPageModule)
      },
      {
        path: 'ai-models',
        loadChildren: () => import('./pages/admin-ai-models/admin-ai-models.module').then(m => m.AdminAiModelsPageModule)
      },
      {
        path: 'ai-conversation/:id',
        loadChildren: () => import('./pages/admin-ai-conversation/admin-ai-conversation.module').then(m => m.AdminAiConversationPageModule)
      }
    ]
  },
  {
    path: 'support',
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/support/support.module').then(m => m.SupportPageModule)
      },
      {
        path: 'my-tickets',
        loadChildren: () => import('./pages/support-tickets/support-tickets.module').then(m => m.SupportTicketsPageModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'ticket/:id',
        loadChildren: () => import('./pages/support-ticket/support-ticket.module').then(m => m.SupportTicketPageModule),
        canActivate: [AuthGuard]
      },
    ]
  },
  {
    path: 'register/stripe-success',
    loadChildren: () => import('./pages/stripe-success/stripe-success.module').then(m => m.StripeSuccessPageModule)
  },
  {
    path: 'invite-register/:token',
    loadChildren: () => import('./pages/invite-register/invite-register.module').then( m => m.InviteRegisterPageModule)
  },


];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule { }
